import {Component, Inject, OnDestroy} from '@angular/core';
import {MAT_DIALOG_DATA, MatDialogRef} from '@angular/material/dialog';
import {IncidentTransaction, Participant, SubjectType} from '@npck/af-frontend-api';
import {LocalizationService, ShortenFullNamePipe} from '@npck/libs/commons';
import {
    Checkbox,
    Column,
    ColumnType,
    ELLIPSIS_SMALL_TEXT_MAX_LENGTH,
    Row,
} from '../../../../../components/full-table/table/table.component';
import {DictionaryHolderService} from '../../../../../domain/dictionary-holder.service';

@Component({
    standalone: false,
    templateUrl: './refund-transactions-dialog.component.html',
    styleUrl: './refund-transactions-dialog.component.scss',
})
export class RefundTransactionsDialogComponent implements OnDestroy {
    dataSource: Row[] = [];
    selectedTransactions: IncidentTransaction[] = [];
    private shortenFullNamePipe = new ShortenFullNamePipe();

    constructor(
        @Inject(MAT_DIALOG_DATA) public dialogData: RefundTransactionDialogData,
        public dialogRef: MatDialogRef<RefundTransactionsDialogComponent>,
        public dictionaryHolderService: DictionaryHolderService,
        private localizationService: LocalizationService,
    ) {
        this.selectedTransactions = [];
        this.buildDataSource(this.dialogData.transactions);
    }

    ngOnDestroy(): void {
        this.selectedTransactions = [];
    }

    private buildDataSource(response: IncidentTransaction[]): void {
        const modifiedResponse = response.map((item, index) => <IncidentTransactionViewModel>{
            ...item,
            index: index + 1,
        });
        this.dataSource = modifiedResponse.map((item, index) => {
            const senderName = this.extractParticipantName(item.sender);
            const receiverName = this.extractParticipantName(item.receiver);
            const isSelected =
                this.dialogData.selectedTransactionIds?.some(transactionId => transactionId === item.id) ?? false;
            if (isSelected) {
                this.selectedTransactions.push(item);
            }
            return <Row>{
                rowStyle: item.disabled ? 'disabled' : undefined,
                select: <Checkbox>{
                    type: ColumnType.CHECKBOX,
                    isChecked: isSelected,
                    isDisabled: item.disabled,
                    tooltip: item.disabled ? {
                        text: this.localizationService.getLocalizedKey('i18n_af_tooltipCouldNotSelectRefundTransaction', {
                            index: (index + 1).toString(),
                        }),
                    } : undefined,
                    change: (checked: boolean) => {
                        if (checked) {
                            this.selectedTransactions.push(item);
                        } else {
                            const transactionIndex = this.selectedTransactions.indexOf(item);
                            this.selectedTransactions.splice(transactionIndex, 1);
                        }
                    },
                },
                transactionIndex: <Column>{
                    type: ColumnType.TEXT,
                    text: this.localizationService.getLocalizedKey('i18n_af_labelIncidentTransactionIndex', {
                        index: (index + 1).toString(),
                    }),
                },
                amount: item.amount == null
                    ? <Column>{
                        type: ColumnType.TEXT,
                        text: '-',
                    }
                    : <Column>{
                        type: ColumnType.MONEY,
                        text: item.amount.toString(),
                    },
                currency: <Column>{
                    type: ColumnType.TEXT,
                    text: item.currency,
                },
                date: <Column>{
                    type: ColumnType.DATETIME,
                    text: item.date,
                },
                senderName: <Column>{
                    type: ColumnType.TEXT,
                    text: senderName,
                    cellStyle: senderName?.length > ELLIPSIS_SMALL_TEXT_MAX_LENGTH && {
                        ellipsisText: true,
                    },
                    tooltip: senderName?.length > ELLIPSIS_SMALL_TEXT_MAX_LENGTH ? {
                        text: senderName,
                    } : undefined,
                },
                senderIin: <Column>{
                    type: ColumnType.TEXT,
                    text: item.sender.identification_number,
                },
                receiverName: <Column>{
                    type: ColumnType.TEXT,
                    text: receiverName,
                    cellStyle: receiverName?.length > ELLIPSIS_SMALL_TEXT_MAX_LENGTH && {
                        ellipsisText: true,
                    },
                    tooltip: receiverName?.length > ELLIPSIS_SMALL_TEXT_MAX_LENGTH ? {
                        text: receiverName,
                    } : undefined,
                },
                receiverIin: <Column>{
                    type: ColumnType.TEXT,
                    text: item.receiver.identification_number,
                },
                isSelected: isSelected,
            };
        });
    }

    private extractParticipantName(participant: Participant): string {
        const participantName = [participant.info.surname, participant.info.name, participant.info.patronymic]
            .filter(name => !!name).join(' ');
        if (participant.type === SubjectType.PERSON) {
            return this.shortenFullNamePipe.transform(participantName);
        }
        return participantName;
    }
}

export interface RefundTransactionDialogData {
    transactions: IncidentTransaction[];
    selectedTransactionIds: string[];
}

export interface IncidentTransactionViewModel extends IncidentTransaction {
    index: number;
}
